/**
 * The tree-guarantee seal on a `WorkshopList` card.
 *
 * Count and icon only. No species text — our data has no species for a
 * replacement to show: the count comes from the warehouse as a per-workshop
 * aggregate, a species would have to come from a note on the operational
 * booking, and nothing joins the two today. That join is its own piece of
 * work, tracked separately.
 *
 * Colours are the design's own brand gold: --warm-gold fills the pill and
 * --gold draws its edge.
 *
 * The icon is this repo's tree glyph rather than the design's, whose `Icon`
 * module was never synced down — the calendar chip already had one, and two
 * tree glyphs in one app is worse than a substituted one.
 */
import type { WorkshopListSize } from './workshopListSizes';
import { WORKSHOP_LIST_SIZES } from './workshopListSizes';
import { WorkshopsCalendarChipTreeIcon } from './WorkshopsCalendarChipTreeIcon';

export interface WorkshopListSealProps {
  show: boolean;
  count?: number;
  size?: WorkshopListSize;
}

export function WorkshopListSeal({ show, count, size = 'md' }: WorkshopListSealProps) {
  if (!show || !count) return null;
  const fontSize = WORKSHOP_LIST_SIZES[size].count;
  return (
    <span
      title={`${count} tree replacement${count === 1 ? '' : 's'}`}
      className="nb-tabular"
      style={{
        marginLeft: 'auto',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize,
        fontWeight: 800,
        color: 'var(--nb-bark-900)',
        background: 'var(--nb-warm-gold)',
        border: '1px solid var(--nb-gold)',
        borderRadius: 999,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 2px rgba(186,117,23,.25)',
      }}
    >
      {count}
      <WorkshopsCalendarChipTreeIcon size={fontSize + 3} />
    </span>
  );
}
